import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Video, VideoOff, Mic, MicOff, Users, Calendar, Clock, 
  Play, Square, ArrowLeft, Send, Plus, Copy, Check,
  Music, GraduationCap, Radio, ChevronRight, User, Star, Pencil, Drum, Footprints
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { LiveKitVideoWidget } from '../components/LiveKitVideo';
import { useMembership } from '@/context/MembershipContext';
import { useAuth } from '@/context/AuthContext';
import { useMasterAccess } from '@/hooks/useMasterAccess';
import { SambaLessons } from '@/pages/SambaLessons';
import BackingTracksGrid from '@/components/play-along/BackingTracksGrid';
import { PageEditProvider, usePageEditMode } from '@/context/PageEditModeContext';
import { PageEditable } from '@/components/edit/PageEditable';
import { useHero } from '@/hooks/useHero';

import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { AdminEditorLink } from '@/components/edit/AdminEditorLink';
import { EditableLink } from '@/components/edit/EditableLink';
import AdminFreeAccessBar, { generateStudioRoomCode } from '@/components/admin/AdminFreeAccessBar';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Instrument Card Component
const InstrumentCard = ({ instrument, selected, onClick, adminEditing, onEditImage, onEditName, onEditDescription }) => (
  <motion.button
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    onClick={adminEditing ? undefined : onClick}
    className={`relative p-4 rounded-xl border-2 transition-all text-left min-h-[140px] ${
      selected 
        ? 'border-primary bg-primary/10' 
        : 'border-stone-700 bg-stone-800/50 hover:border-stone-600'
    }`}
  >
    <div className="w-16 h-16 mx-auto mb-2 rounded-full overflow-hidden bg-stone-700 relative group">
      {instrument.image ? (
        <img loading="lazy" decoding="async" src={instrument.image} alt={instrument.name} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Music className="w-8 h-8 text-stone-400" />
        </div>
      )}
      {adminEditing && (
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); onEditImage(instrument); }}
          className="absolute inset-0 flex items-center justify-center bg-black/70 opacity-0 group-hover:opacity-100 text-yellow-300 cursor-pointer"
          title="Change instrument image"
        >
          <Pencil className="w-4 h-4" />
        </span>
      )}
    </div>
    {adminEditing ? (
      <input
        defaultValue={instrument.name}
        onClick={(e) => e.stopPropagation()}
        onBlur={(e) => onEditName(instrument, e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
        className={`w-full text-sm font-bold text-center bg-stone-900 border border-yellow-400/70 rounded px-1 ${selected ? 'text-primary' : 'text-white'}`}
      />
    ) : (
      <p className={`text-sm font-bold text-center ${selected ? 'text-primary' : 'text-white'}`}>
        {instrument.name}
      </p>
    )}
    {adminEditing ? (
      <textarea
        defaultValue={instrument.description || ''}
        rows={2}
        placeholder="Description"
        onClick={(e) => e.stopPropagation()}
        onBlur={(e) => onEditDescription(instrument, e.target.value)}
        className="mt-1.5 w-full text-xs bg-stone-900 border border-yellow-400/70 rounded px-1 text-stone-300"
      />
    ) : (
      instrument.description && (
        <p className="text-xs text-stone-400 mt-1.5 text-center line-clamp-2 leading-relaxed">
          {instrument.description}
        </p>
      )
    )}
  </motion.button>
);

// Schedule Slot Card
const SlotCard = ({ slot, onBook }) => {
  const isPrivate = slot.type === 'private';
  const dateObj = new Date(slot.date + 'T' + slot.time);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-stone-800/50 rounded-xl p-4 border border-stone-700 hover:border-primary/50 transition-all"
    >
      <div className="flex justify-between items-start mb-3">
        <div>
          <p className="text-white font-medium">
            {dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
          </p>
          <p className="text-2xl font-bold text-primary">{slot.time}</p>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          isPrivate ? 'bg-amber-500/20 text-amber-400' : 'bg-green-500/20 text-green-400'
        }`}>
          <PageEditable path={isPrivate ? "sessionCard.privatePill" : "sessionCard.groupPill"} fallback={isPrivate ? '1-on-1' : 'Group'} />
        </span>
      </div>
      
      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
        <span className="flex items-center gap-1">
          <Clock className="w-4 h-4" />
          {slot.duration} min
        </span>
        <span className="flex items-center gap-1">
          <Users className="w-4 h-4" />
          {slot.spots_left} spot{slot.spots_left !== 1 ? 's' : ''} left
        </span>
      </div>
      
      <div className="flex justify-between items-center">
        <span className="text-xl font-bold text-white">${slot.price}</span>
        <button
          onClick={() => onBook(slot)}
          disabled={slot.spots_left <= 0}
          className="px-4 py-2 bg-primary text-black font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PageEditable path={slot.spots_left > 0 ? "sessionCard.bookNowCta" : "sessionCard.fullLabel"} fallback={slot.spots_left > 0 ? 'Book Now' : 'Full'} />
        </button>
      </div>
    </motion.div>
  );
};

// Active Session Card
const ActiveSessionCard = ({ session, onJoin }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className="bg-gradient-to-br from-primary/20 to-amber-500/10 rounded-xl p-5 border border-primary/30"
  >
    <div className="flex items-center gap-2 mb-3">
      <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
      <span className="text-red-400 text-sm font-medium">
        <PageEditable path="liveNow.pill" fallback="LIVE NOW" />
      </span>
    </div>
    
    <h3 className="text-xl font-bold text-white mb-2">{session.title}</h3>
    
    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
      <span className="flex items-center gap-1">
        <User className="w-4 h-4" />
        {session.host}
      </span>
      {session.instrument && (
        <span className="flex items-center gap-1">
          <Music className="w-4 h-4" />
          {session.instrument}
        </span>
      )}
      <span className="flex items-center gap-1">
        <Users className="w-4 h-4" />
        {session.participant_count}/{session.max_students}
      </span>
    </div>
    
    <button
      onClick={() => onJoin(session)}
      disabled={session.spots_left <= 0}
      className="w-full py-3 bg-primary text-black font-bold rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
    >
      <Video className="w-5 h-5" />
      <PageEditable path="liveNow.joinButton" fallback="Join Live Session" />
    </button>
  </motion.div>
);

// Main Live Lessons Page
export default function LiveLessons() {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const isLiveLessons = path.startsWith('/live-lessons');
  const editorTarget = isLiveLessons
    ? '/admin/dashboard#live-lessons-admin-btn'
    : '/admin/dashboard#play-along-editor-btn';
  const editorLabel = isLiveLessons ? 'Edit Live Lessons' : 'Edit Play Along';
  return (
    <PageEditProvider slug="play-along" group="playAlong">
      <LiveLessonsInner />
      <PageEditToolbar />
      <AdminEditorLink to={editorTarget} label={editorLabel} />
    </PageEditProvider>
  );
}

function LiveLessonsInner() {
  const hero = useHero('live-lessons');
  const navigate = useNavigate();
  const { sessionCode: urlSessionCode } = useParams();
  const { isAdminUser, enabled: pageEditEnabled } = usePageEditMode();
  const adminEditing = isAdminUser && pageEditEnabled;
  const [instrumentImageModal, setInstrumentImageModal] = useState(null);
  const [instrumentImageDraft, setInstrumentImageDraft] = useState('');
  const [uploadingInstImg, setUploadingInstImg] = useState(false);

  const openInstrumentImageModal = (inst) => {
    setInstrumentImageDraft(inst.image || '');
    setInstrumentImageModal(inst);
  };

  const saveInstrumentGeneric = async (inst, patch) => {
    if (Object.entries(patch).every(([k, v]) => (inst[k] || '') === (v || ''))) return;
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(`${API}/api/live-lessons/instruments/${inst.id}`, patch, { headers: { Authorization: `Bearer ${token}` } });
      setInstruments(prev => prev.map(x => x.id === inst.id ? { ...x, ...patch } : x));
      toast.success('Saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const saveInstrumentName = async (inst, name) => saveInstrumentGeneric(inst, { name });
  const saveInstrumentDescription = async (inst, description) => saveInstrumentGeneric(inst, { description });
  
  const [view, setView] = useState('main'); // main, schedule, join, session
  const [instruments, setInstruments] = useState([]);
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Session state
  const [sessionCode, setSessionCode] = useState('');
  const [studentName, setStudentName] = useState('');
  const [currentSession, setCurrentSession] = useState(null);
  const [isInSession, setIsInSession] = useState(false);
  const [isHost, setIsHost] = useState(false);
  
  // Booking modal
  const [bookingSlot, setBookingSlot] = useState(null);
  const [bookingForm, setBookingForm] = useState({ name: '', email: '', notes: '' });
  
  // Create session modal (for teachers)
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: 'Live Samba Lesson',
    instrument: '',
    max_students: 5,
    is_group: true
  });

  // ============================================
  // 3-DAY FREE TRIAL LOGIC (admins bypass all gates)
  // ============================================
  const { membership } = useMembership();
  const isAdmin = membership?.isAdmin || membership?.tier === 'admin';
  const [trialStatus, setTrialStatus] = useState({ active: false, daysLeft: 0, expired: false });
  
  useEffect(() => {
    const checkTrial = () => {
      const trialStart = localStorage.getItem('playAlongTrialStart');
      const TRIAL_DAYS = 3;
      
      if (!trialStart) {
        // No trial started yet
        setTrialStatus({ active: false, daysLeft: TRIAL_DAYS, expired: false });
      } else {
        const startDate = new Date(trialStart);
        const now = new Date();
        const daysPassed = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
        const daysLeft = TRIAL_DAYS - daysPassed;
        
        if (daysLeft > 0) {
          setTrialStatus({ active: true, daysLeft, expired: false });
        } else {
          setTrialStatus({ active: false, daysLeft: 0, expired: true });
        }
      }
    };
    
    checkTrial();
  }, []);
  
  const startFreeTrial = () => {
    const now = new Date().toISOString();
    localStorage.setItem('playAlongTrialStart', now);
    setTrialStatus({ active: true, daysLeft: 3, expired: false });
    toast.success('Your 3-day free trial has started! Enjoy full access.');
  };
  
  const hasAccess = isAdmin || trialStatus.active || !trialStatus.expired;

  // Master Play Along access (Master Pack / Master Pass / owned lessons / member)
  const { user, isAdmin: isAuthAdmin } = useAuth();
  const userEmail = user?.email || null;
  const { access: rawMasterAccess, refresh: refreshMasterAccess } = useMasterAccess(userEmail);

  // GLOBAL ADMIN BYPASS: super_admin / admin users get unlimited access to /play-along
  // regardless of membership tier, trial state, or /api/master/access response.
  const isSuperAdmin = typeof isAuthAdmin === 'function' && isAuthAdmin();
  const isAdminAll = isAdmin || isSuperAdmin;
  const masterAccess = isAdminAll
    ? {
        is_admin: true,
        unlocks_all: true,
        has_master_pass: true,
        has_member_access: true,
        master_pack_instruments: ['all'],
        owned_lesson_ids: ['admin-all'],
        live_credits: 999,
      }
    : rawMasterAccess;

  // Purchase handlers
  const buyMasterPass = useCallback(async () => {
    if (!userEmail) {
      toast.error('Please sign in to purchase the Master Pass');
      navigate('/login?return=/play-along');
      return;
    }
    try {
      const res = await axios.post(`${API}/api/master-pass/purchase`, {
        user_email: userEmail,
        price: 199,
      });
      if (res.data?.checkout_url) window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout');
    }
  }, [userEmail, navigate]);

  const buyMasterPack = useCallback(async (instrument) => {
    if (!userEmail) {
      toast.error('Please sign in to purchase');
      navigate('/login?return=/play-along');
      return;
    }
    try {
      const res = await axios.post(`${API}/api/master-pack/purchase`, {
        instrument_id: instrument.id,
        instrument_name: instrument.name,
        user_email: userEmail,
        price: 79,
      });
      if (res.data?.checkout_url) window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout');
    }
  }, [userEmail, navigate]);

  // Handle Stripe return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    const sessionId = params.get('session_id');
    if (success && sessionId && userEmail) {
      (async () => {
        try {
          const res = await axios.post(`${API}/api/master/confirm-purchase`, {
            session_id: sessionId,
            user_email: userEmail,
          });
          if (res.data?.success) {
            toast.success(success === 'pass' ? 'Master Pass unlocked! All content is now yours.' : 'Master Pack unlocked!');
            await refreshMasterAccess();
          }
        } catch (_) { /* ignore */ }
        // Clean the URL
        window.history.replaceState({}, '', '/play-along');
      })();
    } else if (params.get('cancelled')) {
      toast.info('Checkout cancelled');
      window.history.replaceState({}, '', '/play-along');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  // Fetch data
  const [step1Instruments, setStep1Instruments] = useState([]);
  useEffect(() => {
    const load = () => axios.get(`${API}/api/instrument-lessons`)
      .then(r => setStep1Instruments(r.data || []))
      .catch(() => setStep1Instruments([]));
    load();
    const onChanged = () => load();
    window.addEventListener('instrument-lessons-updated', onChanged);
    return () => window.removeEventListener('instrument-lessons-updated', onChanged);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch instruments
        const instRes = await fetch(`${API}/api/live-lessons/instruments`);
        if (instRes.ok) {
          const instData = await instRes.json();
          setInstruments(instData);
        }
        
        // Fetch schedule
        const schedRes = await fetch(`${API}/api/live-lessons/schedule`);
        if (schedRes.ok) {
          const schedData = await schedRes.json();
          setSchedule(schedData);
        }
        
        // Fetch active sessions
        const activeRes = await fetch(`${API}/api/live-lessons/sessions/active/list`);
        if (activeRes.ok) {
          const activeData = await activeRes.json();
          setActiveSessions(activeData);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    
    // Poll for active sessions every 30s
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API}/api/live-lessons/sessions/active/list`);
        if (res.ok) {
          const data = await res.json();
          setActiveSessions(data);
        }
      } catch (err) {}
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Handle URL session code
  useEffect(() => {
    if (urlSessionCode) {
      setSessionCode(urlSessionCode);
      setView('join');
    }
  }, [urlSessionCode]);

  // Join session
  const handleJoinSession = async () => {
    if (!sessionCode || !studentName) {
      toast.error('Please enter session code and your name');
      return;
    }
    
    try {
      const res = await fetch(`${API}/api/live-lessons/sessions/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_code: sessionCode,
          student_name: studentName
        })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to join session');
      }
      
      setCurrentSession(data.session);
      setIsInSession(true);
      setIsHost(false);
      toast.success(`Joined: ${data.session.title}`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Create live session (teacher)
  const handleCreateSession = async () => {
    try {
      const res = await fetch(`${API}/api/live-lessons/sessions/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createForm)
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to create session');
      }
      
      setCurrentSession({
        code: data.session_code,
        title: createForm.title,
        instrument: createForm.instrument
      });
      setSessionCode(data.session_code);
      setIsInSession(true);
      setIsHost(true);
      setShowCreateModal(false);
      toast.success(`Session created! Code: ${data.session_code}`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Book slot
  const handleBookSlot = async () => {
    if (!bookingForm.name || !bookingForm.email) {
      toast.error('Please fill in your name and email');
      return;
    }
    
    try {
      const res = await fetch(`${API}/api/live-lessons/schedule/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slot_id: bookingSlot.id,
          student_name: bookingForm.name,
          student_email: bookingForm.email,
          instrument: selectedInstrument,
          notes: bookingForm.notes
        })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Booking failed');
      }
      
      toast.success('Lesson booked! Check your email for confirmation.');
      setBookingSlot(null);
      setBookingForm({ name: '', email: '', notes: '' });
      
      // Refresh schedule
      const schedRes = await fetch(`${API}/api/live-lessons/schedule`);
      if (schedRes.ok) {
        setSchedule(await schedRes.json());
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  // End session
  const handleEndSession = async () => {
    if (currentSession && isHost) {
      try {
        await fetch(`${API}/api/live-lessons/sessions/${currentSession.code}/end`, {
          method: 'POST'
        });
      } catch (err) {}
    }
    setIsInSession(false);
    setCurrentSession(null);
    setSessionCode('');
  };

  // Copy session code
  const copySessionCode = () => {
    navigator.clipboard.writeText(sessionCode);
    toast.success('Session code copied!');
  };

  // If in session, show video interface
  if (isInSession && currentSession) {
    return (
      <div className="min-h-screen bg-stone-900 text-white" data-testid="live-session-view">
        {/* Session Header */}
        <div className="bg-stone-800 border-b border-stone-700 px-6 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleEndSession}
                className="p-2 hover:bg-stone-700 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="font-bold text-lg">{currentSession.title}</h1>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    LIVE
                  </span>
                  {currentSession.instrument && (
                    <span>• {currentSession.instrument}</span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Session Code */}
              <div className="flex items-center gap-2 bg-stone-700 px-3 py-1.5 rounded-lg">
                <span className="text-sm text-muted-foreground">Code:</span>
                <span className="font-mono font-bold text-primary">{currentSession.code}</span>
                <button onClick={copySessionCode} className="p-1 hover:bg-stone-600 rounded">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              
              {isHost && (
                <button
                  onClick={handleEndSession}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2"
                >
                  <Square className="w-4 h-4" />
                  End Session
                </button>
              )}
            </div>
          </div>
        </div>
        
        {/* Video Area */}
        <div className="flex-1 p-6">
          <div className="max-w-6xl mx-auto">
            <LiveKitVideoWidget
              roomCode={currentSession.code}
              username={isHost ? 'Mestre Curtis' : studentName}
              isHost={isHost}
              onClose={handleEndSession}
              minimizable={false}
            />
          </div>
        </div>
      </div>
    );
  }

  // Main page
  return (
    <div className="min-h-screen bg-stone-900 text-white" data-testid="live-lessons-page">
      <AdminFreeAccessBar
        title="Admin — Play Along Free Access"
        subtitle="Skip trial, membership and payment. Enter a live studio room in one click."
        testid="admin-bar-play-along"
        primary={{
          label: 'Enter Studio Now',
          icon: Video,
          onClick: () => {
            const code = generateStudioRoomCode();
            window.location.href = `/video-room/${code}/host`;
          },
        }}
        secondary={{
          label: 'Practice Tracks',
          onClick: () => {
            const el = document.getElementById('step-2');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          },
        }}
      />
      {/* Hero Section */}
      <section className="relative py-16 px-6 bg-gradient-to-b from-primary/10 to-transparent">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary/20 rounded-full text-primary text-sm font-medium mb-6"
          >
            <Video className="w-4 h-4" />
            <PageEditable path="hero.eyebrow" fallback={hero.kicker || "Live Lessons · Drumming + Dance"} />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-4"
          >
            <PageEditable path="hero.title" fallback={hero.title_top || "Live Lessons"} /> <span className="text-primary"><PageEditable path="hero.titleAccent" fallback={hero.title_bottom || "with the Master"} /></span>
          </motion.h1>
          
          <PageEditable
            path="hero.subtitle"
            as="p"
            multiline
            className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8"
            fallback="Learn Brasilian drumming AND Samba dance one-on-one with Mestre Curtis Pierre — live, over video. Pick your path below."
          />

          {/* CHOOSE YOUR PATH — Drumming vs Dance */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="max-w-5xl mx-auto grid md:grid-cols-2 gap-4 mb-10"
            data-testid="path-picker"
          >
            {/* DRUMMING path — scrolls to the Play Along journey below */}
            <a
              href="#drumming-journey"
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById('drumming-journey');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className="group text-left bg-gradient-to-br from-primary/15 to-amber-500/5 border-2 border-primary/30 hover:border-primary rounded-2xl p-6 transition-all"
              data-testid="path-drumming"
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Drum className="w-7 h-7 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary/20 text-primary text-[10px] font-bold uppercase tracking-widest mb-2">
                    <Radio className="w-3 h-3" /> Live
                  </div>
                  <h2 className="text-xl font-black text-white mb-1 group-hover:text-primary transition-colors">
                    <PageEditable path="drummingPath.title" fallback="Live Drumming Lessons" />
                  </h2>
                  <PageEditable
                    path="drummingPath.desc"
                    as="p"
                    multiline
                    className="text-sm text-muted-foreground mb-3"
                    fallback="3-step Play Along journey — learn the technique, practice with backing tracks at home, then jam live over video with the Master."
                  />
                  <span className="inline-flex items-center gap-1 text-primary text-sm font-bold">
                    Start the journey <ChevronRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </a>

            {/* DANCE path — routes to /classes private lessons */}
            <button
              onClick={() => navigate('/classes#private')}
              className="group text-left bg-gradient-to-br from-pink-500/15 to-red-500/5 border-2 border-pink-500/30 hover:border-pink-500 rounded-2xl p-6 transition-all"
              data-testid="path-dance"
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl bg-pink-500/20 flex items-center justify-center flex-shrink-0">
                  <Footprints className="w-7 h-7 text-pink-400" />
                </div>
                <div className="flex-1">
                  <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-pink-500/20 text-pink-400 text-[10px] font-bold uppercase tracking-widest mb-2">
                    <Radio className="w-3 h-3" /> Live
                  </div>
                  <h2 className="text-xl font-black text-white mb-1 group-hover:text-pink-300 transition-colors">
                    <PageEditable path="dancePath.title" fallback="Live Samba Dance Lessons" />
                  </h2>
                  <PageEditable
                    path="dancePath.desc"
                    as="p"
                    multiline
                    className="text-sm text-muted-foreground mb-3"
                    fallback="One-on-one Samba, Afro-Brasilian and Stilt dance instruction with the Master — book a private live session that fits your schedule."
                  />
                  <span className="inline-flex items-center gap-1 text-pink-400 text-sm font-bold">
                    Book a dance lesson <ChevronRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </button>
          </motion.div>

          {/* Drumming Journey anchor */}
          <div id="drumming-journey" className="scroll-mt-24">
            <div className="max-w-3xl mx-auto mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 text-primary text-xs font-bold uppercase tracking-widest">
                <Drum className="w-3.5 h-3.5" />
                <PageEditable path="drummingJourney.eyebrow" fallback="Drumming Path — Play Along with the Master" />
              </div>
              <PageEditable
                path="drummingJourney.subtitle"
                as="p"
                multiline
                className="text-sm text-muted-foreground mt-3"
                fallback="A 3-step live drumming journey: master the technique, drum along at home, then jam face-to-face over video."
              />
            </div>
          </div>
          
          {/* 3-Step Journey */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="max-w-5xl mx-auto grid md:grid-cols-3 gap-4 mb-8"
          >
            {[
              { n: 1, defTitle: 'Take the Lesson', defDesc: 'Watch Mestre Curtis teach the technique for your chosen instrument.', anchor: '#step-1', color: 'from-blue-500 to-indigo-500' },
              { n: 2, defTitle: 'Solo Play Along', defDesc: 'Practice with backing tracks + slow/medium/fast tempo videos at home.', anchor: '#step-2', color: 'from-primary to-amber-500' },
              { n: 3, defTitle: 'Live Play Along', defDesc: 'Two-way video session — jam live with the Master, see each other in real time.', anchor: '#step-3', color: 'from-red-500 to-pink-500' },
            ].map((s) => (
              <a
                key={s.n}
                href={s.anchor}
                className="group text-left bg-stone-800/60 backdrop-blur-sm rounded-2xl border border-stone-700 hover:border-primary p-6 transition-all"
              >
                <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${s.color} flex items-center justify-center text-white font-bold text-xl mb-3`}>
                  {s.n}
                </div>
                <h3 className="font-bold text-white mb-1 group-hover:text-primary transition-colors">
                  Step {s.n} — <PageEditable path={`steps.${s.n}.title`} fallback={s.defTitle} />
                </h3>
                <PageEditable path={`steps.${s.n}.desc`} as="p" multiline className="text-sm text-muted-foreground" fallback={s.defDesc} />
              </a>
            ))}
          </motion.div>

          {/* Bundle pricing banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.28 }}
            className="max-w-5xl mx-auto grid md:grid-cols-2 gap-4 mb-8"
            data-testid="master-pack-banner"
          >
            <div className="bg-gradient-to-br from-primary/20 to-amber-500/10 border-2 border-primary/40 rounded-2xl p-6 text-left">
              <div className="flex items-center gap-2 text-primary text-xs font-bold tracking-widest mb-2">
                <Music className="w-4 h-4" /> <PageEditable path="pack.label" fallback="MASTER PACK" />
              </div>
              <h3 className="text-2xl font-black text-white mb-1">
                <PageEditable path="pack.price" fallback="$79" /> <span className="text-sm font-normal text-muted-foreground"><PageEditable path="pack.priceSuffix" fallback="/ instrument" /></span>
              </h3>
              <PageEditable path="pack.description" as="p" multiline className="text-sm text-muted-foreground mb-4" fallback="All 3 steps for ONE instrument — lesson video, backing tracks, and 1 live session credit." />
              <a href="#step-1" className="inline-flex items-center gap-2 px-5 py-2 bg-primary hover:bg-primary/90 text-black font-bold rounded-full text-sm">
                <PageEditable path="pack.cta" fallback="Choose an instrument" /> <ChevronRight className="w-4 h-4" />
              </a>
            </div>
            <div className="bg-gradient-to-br from-red-500/20 to-pink-500/10 border-2 border-red-500/40 rounded-2xl p-6 text-left relative overflow-hidden">
              <div className="absolute top-3 right-3 text-[10px] font-bold uppercase tracking-widest bg-red-500 text-white rounded-full px-2 py-0.5">
                <PageEditable path="pass.badge" fallback="Best value" />
              </div>
              <div className="flex items-center gap-2 text-red-400 text-xs font-bold tracking-widest mb-2">
                <Star className="w-4 h-4" /> <PageEditable path="pass.label" fallback="ALL-ACCESS MASTER PASS" />
              </div>
              <h3 className="text-2xl font-black text-white mb-1">
                <PageEditable path="pass.price" fallback="$199" /> <span className="text-sm font-normal text-muted-foreground"><PageEditable path="pass.priceSuffix" fallback="one-time" /></span>
              </h3>
              <PageEditable path="pass.description" as="p" multiline className="text-sm text-muted-foreground mb-4" fallback="Every instrument, every step + unlimited live sessions with the Master." />
              <button
                onClick={buyMasterPass}
                className="inline-flex items-center gap-2 px-5 py-2 bg-red-500 hover:bg-red-400 text-white font-bold rounded-full text-sm"
                data-testid="buy-master-pass-btn"
              >
                <PageEditable path="pass.cta" fallback="Get the Pass" /> <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
          
          {/* Members-only banner */}
          <PageEditable
            path="membersBanner"
            as="p"
            multiline
            className="text-xs text-muted-foreground mb-2"
            fallback="Site members: Steps 1 & 2 are included with your membership — 1 free live session per month."
          />
          
          {/* 3-Day Free Trial Banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.28 }}
            className="max-w-4xl mx-auto mb-8"
          >
            {!trialStatus.active && !trialStatus.expired && (
              <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 border border-green-500/50 rounded-2xl p-6 text-center">
                <PageEditable path="trial.title" as="h3" className="text-xl font-bold text-white mb-2" fallback="Try It Free for 3 Days!" />
                <PageEditable path="trial.description" as="p" multiline className="text-muted-foreground mb-4" fallback="Get full access to all Play Along features. No credit card required." />
                <button
                  onClick={startFreeTrial}
                  className="px-8 py-3 bg-green-500 text-black font-bold rounded-xl hover:bg-green-400 transition-colors"
                  data-testid="start-trial-btn"
                >
                  <PageEditable path="trial.cta" fallback="Start Free Trial" />
                </button>
              </div>
            )}
            
            {trialStatus.active && (
              <div className="bg-gradient-to-r from-primary/20 to-amber-500/20 border border-primary/50 rounded-2xl p-4 text-center">
                <div className="flex items-center justify-center gap-2 text-primary font-bold">
                  <Star className="w-5 h-5" />
                  <span><PageEditable path="trial.activePrefix" fallback="Free Trial Active" /> — {trialStatus.daysLeft} day{trialStatus.daysLeft !== 1 ? 's' : ''} remaining</span>
                </div>
                <PageEditable path="trial.activeNote" as="p" className="text-sm text-muted-foreground mt-1" fallback="Enjoy full access to all features!" />
              </div>
            )}
            
            {trialStatus.expired && !isAdminAll && (
              <div className="bg-gradient-to-r from-red-600/20 to-orange-600/20 border border-red-500/50 rounded-2xl p-6 text-center">
                <PageEditable path="trial.expiredTitle" as="h3" className="text-xl font-bold text-white mb-2" fallback="Your Free Trial Has Ended" />
                <PageEditable path="trial.expiredDescription" as="p" multiline className="text-muted-foreground mb-4" fallback="Subscribe to continue playing along with the Master." />
                <button
                  onClick={() => navigate('/membership')}
                  className="px-8 py-3 bg-primary text-black font-bold rounded-xl hover:bg-primary/90 transition-colors"
                >
                  <PageEditable path="trial.expiredCta" fallback="View Membership Options" />
                </button>
              </div>
            )}
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-wrap justify-center gap-4"
          >
            {/* Trial users get Practice Mode access */}
            {(trialStatus.active || !trialStatus.expired) && (
              <button
                onClick={() => {
                  if (!trialStatus.active && !trialStatus.expired) {
                    startFreeTrial();
                  }
                  navigate('/play-along-videos');
                }}
                className="px-6 py-3 bg-green-500 text-black font-bold rounded-xl hover:bg-green-400 transition-colors flex items-center gap-2"
                data-testid="practice-mode-btn"
              >
                <Play className="w-5 h-5" />
                <PageEditable path={trialStatus.active ? "buttons.practiceMode" : "buttons.startTrialPractice"} fallback={trialStatus.active ? 'Enter Practice Mode' : 'Start Trial & Practice'} />
              </button>
            )}
            
            <button
              onClick={() => setView('join')}
              className="px-6 py-3 bg-primary text-black font-bold rounded-xl hover:bg-primary/90 transition-colors flex items-center gap-2"
              data-testid="join-session-btn"
            >
              <Video className="w-5 h-5" />
              <PageEditable path="buttons.joinLive" fallback="Join Live Session" />
            </button>
            <button
              onClick={() => setView('schedule')}
              className="px-6 py-3 bg-stone-700 text-white font-bold rounded-xl hover:bg-stone-600 transition-colors flex items-center gap-2"
              data-testid="book-lesson-btn"
            >
              <Calendar className="w-5 h-5" />
              <PageEditable path="buttons.bookLesson" fallback="Book a Lesson" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* ========== STEP 1 — TAKE THE LESSON ========== */}
      <section id="step-1" className="py-12 border-t border-stone-800 scroll-mt-24">
        <div className="max-w-6xl mx-auto px-6 mb-8">
          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-bold text-xl">1</div>
            <div>
              <div className="text-xs font-bold text-blue-400 uppercase tracking-widest">
                <PageEditable path="step1.eyebrow" fallback="Step 1" />
              </div>
              <PageEditable path="step1.title" as="h2" className="text-3xl font-black text-white" fallback="Take the Lesson" />
            </div>
          </div>
          <PageEditable path="step1.subtitle" as="p" multiline className="text-muted-foreground pl-16" fallback="Buy an instrument lesson to unlock Steps 2 & 3 for that instrument. Members: included with your plan." />
        </div>
        <SambaLessons embedded />
      </section>

      {/* ========== STEP 2 — SOLO PLAY ALONG ========== */}
      <section id="step-2" className="py-16 border-t border-stone-800 bg-gradient-to-b from-transparent to-stone-950/60 scroll-mt-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-amber-500 flex items-center justify-center text-black font-bold text-xl">2</div>
            <div>
              <div className="text-xs font-bold text-primary uppercase tracking-widest">
                <PageEditable path="step2.eyebrow" fallback="Step 2" />
              </div>
              <PageEditable path="step2.title" as="h2" className="text-3xl font-black text-white" fallback="Solo Play Along" />
            </div>
          </div>
          <PageEditable path="step2.subtitle" as="p" multiline className="text-muted-foreground pl-16 mb-8" fallback="Practice at home with backing tracks + slow / medium / fast tempo videos. Unlocked after you take the matching Step 1 lesson." />
          <BackingTracksGrid access={masterAccess} instruments={step1Instruments} onBuyPack={buyMasterPack} />
        </div>
      </section>

      {/* ========== STEP 3 — LIVE PLAY ALONG ========== */}
      <section id="step-3" className="py-16 border-t border-stone-800 scroll-mt-24">
        <div className="max-w-6xl mx-auto px-6 mb-8">
          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center text-white font-bold text-xl">3</div>
            <div>
              <div className="text-xs font-bold text-red-400 uppercase tracking-widest">
                <PageEditable path="step3.eyebrow" fallback="Step 3" />
              </div>
              <PageEditable path="step3.title" as="h2" className="text-3xl font-black text-white" fallback="Live Play Along" />
            </div>
          </div>
          <PageEditable path="step3.subtitle" as="p" multiline className="text-muted-foreground pl-16" fallback="Two-way video jam session with Mestre Curtis. See the Master, be seen by the Master. Live audio, live rhythm, real time." />
        </div>

        {/* Active Sessions */}
      {activeSessions.length > 0 && (
        <section className="px-6 py-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Radio className="w-6 h-6 text-red-500" />
              <PageEditable path="liveNow.title" fallback="Live Now" />
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeSessions.map((session) => (
                <ActiveSessionCard
                  key={session.code}
                  session={session}
                  onJoin={(s) => {
                    setSessionCode(s.code);
                    setView('join');
                  }}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {activeSessions.length === 0 && (
        <div className="max-w-6xl mx-auto px-6 pb-4">
          <div className="bg-stone-800/40 border border-stone-700 rounded-2xl p-6 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <PageEditable path="noLive.title" as="p" className="text-white font-semibold" fallback="No live sessions right now." />
              <p className="text-sm text-muted-foreground">
                {masterAccess?.unlocks_all ? (
                  <PageEditable path="noLive.unlimited" fallback="Unlimited access as a Master Pass / member." />
                ) : (masterAccess?.live_credits > 0) ? (
                  <>You have <b className="text-primary">{masterAccess.live_credits}</b> live session credit{masterAccess.live_credits === 1 ? '' : 's'}.</>
                ) : (
                  <PageEditable path="noLive.needAccess" multiline fallback="Book a private slot or join the next scheduled group session below. Requires a Step 1 lesson or Master Pass." />
                )}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  if (!masterAccess) { toast.error('Please sign in'); navigate('/login?return=/play-along'); return; }
                  if (!masterAccess.unlocks_all && masterAccess.live_credits <= 0 && (masterAccess.owned_lesson_ids || []).length === 0) {
                    toast.error('Take a Step 1 lesson or get the Master Pass to join live sessions.');
                    document.querySelector('#step-1')?.scrollIntoView({ behavior: 'smooth' });
                    return;
                  }
                  setView('join');
                }}
                className="px-4 py-2 bg-primary text-black font-semibold rounded-full text-sm"
                data-testid="join-live-btn"
              >
                <PageEditable path="noLive.joinCta" fallback="Join with code" />
              </button>
              <button
                onClick={() => {
                  if (!masterAccess) { toast.error('Please sign in'); navigate('/login?return=/play-along'); return; }
                  if (!masterAccess.unlocks_all && (masterAccess.owned_lesson_ids || []).length === 0) {
                    toast.error('Take a Step 1 lesson first to book a live session.');
                    document.querySelector('#step-1')?.scrollIntoView({ behavior: 'smooth' });
                    return;
                  }
                  setView('schedule');
                }}
                className="px-4 py-2 bg-stone-700 text-white font-semibold rounded-full text-sm"
                data-testid="book-live-btn"
              >
                <PageEditable path="noLive.bookCta" fallback="Book a session" />
              </button>
            </div>
          </div>
        </div>
      )}
      </section>

      {/* Join Session View */}
      <AnimatePresence mode="wait">
        {view === 'join' && (
          <motion.section
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="px-6 py-12"
          >
            <div className="max-w-md mx-auto">
              <button
                onClick={() => setView('main')}
                className="flex items-center gap-2 text-muted-foreground hover:text-white mb-6"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              
              <div className="bg-stone-800 rounded-2xl p-6 border border-stone-700">
                <h2 className="text-2xl font-bold mb-6 text-center">Join Live Session</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Session Code</label>
                    <input
                      type="text"
                      value={sessionCode}
                      onChange={(e) => setSessionCode(e.target.value.toUpperCase())}
                      placeholder="Enter 6-digit code"
                      className="w-full px-4 py-3 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none text-center text-2xl font-mono tracking-widest"
                      maxLength={6}
                      data-testid="session-code-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Your Name</label>
                    <input
                      type="text"
                      value={studentName}
                      onChange={(e) => setStudentName(e.target.value)}
                      placeholder="Enter your name"
                      className="w-full px-4 py-3 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                      data-testid="student-name-input"
                    />
                  </div>
                  
                  <button
                    onClick={handleJoinSession}
                    disabled={!sessionCode || !studentName}
                    className="w-full py-3 bg-primary text-black font-bold rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="join-btn"
                  >
                    <Video className="w-5 h-5" />
                    Join Session
                  </button>
                </div>
              </div>
            </div>
          </motion.section>
        )}

        {view === 'schedule' && (
          <motion.section
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="px-6 py-12"
          >
            <div className="max-w-6xl mx-auto">
              <button
                onClick={() => setView('main')}
                className="flex items-center gap-2 text-muted-foreground hover:text-white mb-6"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              
              <h2 className="text-2xl font-bold mb-6">Book a Lesson</h2>
              
              {/* Instrument Filter */}
              <div className="mb-8">
                <h3 className="text-lg font-medium mb-4">Select Instrument (Optional)</h3>
                <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-10 gap-3">
                  {instruments.map((inst) => (
                    <InstrumentCard
                      key={inst.id}
                      instrument={inst}
                      selected={selectedInstrument === inst.id}
                      onClick={() => setSelectedInstrument(
                        selectedInstrument === inst.id ? null : inst.id
                      )}
                      adminEditing={adminEditing}
                      onEditName={saveInstrumentName}
                      onEditDescription={saveInstrumentDescription}
                      onEditImage={openInstrumentImageModal}
                    />
                  ))}
                </div>
              </div>
              
              {/* Schedule Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {schedule.map((slot) => (
                  <SlotCard
                    key={slot.id}
                    slot={slot}
                    onBook={setBookingSlot}
                  />
                ))}
              </div>
            </div>
          </motion.section>
        )}

        {view === 'main' && (
          <motion.section
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="px-6 py-12"
          >
            <div className="max-w-6xl mx-auto">
              {/* Features - Now Clickable */}
              <div className="grid md:grid-cols-3 gap-6 mb-12">
                <motion.button
                  whileHover={{ scale: 1.02, y: -4 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setView('join')}
                  className="bg-stone-800/50 rounded-xl p-6 border border-stone-700 hover:border-primary/50 transition-all text-left group"
                  data-testid="feature-watch-learn"
                >
                  <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary/30 transition-colors">
                    <Video className="w-6 h-6 text-primary" />
                  </div>
                  <PageEditable path="features.watchLearn.title" as="h3" className="text-lg font-bold mb-2 group-hover:text-primary transition-colors" fallback="Watch & Learn" />
                  <PageEditable path="features.watchLearn.description" as="p" multiline className="text-muted-foreground text-sm" fallback="Watch Mestre Curtis demonstrate rhythms in real-time with multiple camera angles." />
                  <div className="mt-4 text-primary text-sm font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    Join Session <ChevronRight className="w-4 h-4" />
                  </div>
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.02, y: -4 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setView('schedule')}
                  className="bg-stone-800/50 rounded-xl p-6 border border-stone-700 hover:border-green-500/50 transition-all text-left group"
                  data-testid="feature-practice-feedback"
                >
                  <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-green-500/30 transition-colors">
                    <Users className="w-6 h-6 text-green-500" />
                  </div>
                  <PageEditable path="features.practice.title" as="h3" className="text-lg font-bold mb-2 group-hover:text-green-500 transition-colors" fallback="Practice & Feedback" />
                  <PageEditable path="features.practice.description" as="p" multiline className="text-muted-foreground text-sm" fallback="Play along and receive real-time feedback on your technique and timing." />
                  <div className="mt-4 text-green-500 text-sm font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    Book a Lesson <ChevronRight className="w-4 h-4" />
                  </div>
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.02, y: -4 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setView('join')}
                  className="bg-stone-800/50 rounded-xl p-6 border border-stone-700 hover:border-amber-500/50 transition-all text-left group"
                  data-testid="feature-play-together"
                >
                  <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-amber-500/30 transition-colors">
                    <Star className="w-6 h-6 text-amber-500" />
                  </div>
                  <PageEditable path="features.together.title" as="h3" className="text-lg font-bold mb-2 group-hover:text-amber-500 transition-colors" fallback="Play Together" />
                  <PageEditable path="features.together.description" as="p" multiline className="text-muted-foreground text-sm" fallback="When conditions allow, play along live with the Master in real-time." />
                  <div className="mt-4 text-amber-500 text-sm font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    Join Live <ChevronRight className="w-4 h-4" />
                  </div>
                </motion.button>
              </div>

              {/* $2 Test Purchase - Play Along */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-12 bg-gradient-to-r from-green-600/20 to-emerald-600/20 border-2 border-green-500/50 rounded-2xl p-6"
              >
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                      <Star className="w-8 h-8 text-green-400" />
                    </div>
                    <div>
                      <PageEditable path="testPurchase.eyebrow" as="span" className="text-green-400 font-semibold text-sm uppercase tracking-wider" fallback="Test Purchase" />
                      <PageEditable path="testPurchase.title" as="h3" className="text-xl font-bold text-foreground" fallback="Test Live Lesson Checkout - $2.00" />
                      <PageEditable path="testPurchase.description" as="p" multiline className="text-muted-foreground text-sm" fallback="Verify the lesson booking checkout flow with a $2 test transaction" />
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API}/api/live-lessons/test-purchase`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            item_name: 'Test Live Lesson Purchase',
                            price: 2.00
                          })
                        });
                        if (res.ok) {
                          const data = await res.json();
                          if (data.checkout_url) {
                            window.location.href = data.checkout_url;
                          }
                        } else {
                          toast.error('Failed to start checkout');
                        }
                      } catch (err) {
                        toast.error('Checkout error');
                      }
                    }}
                    className="px-8 py-3 bg-green-500 hover:bg-green-400 text-black rounded-full font-bold text-lg transition-all hover:scale-105"
                    data-testid="test-buy-live-lesson-2"
                  >
                    <PageEditable path="testPurchase.cta" fallback="Buy Test - $2.00" />
                  </button>
                </div>
              </motion.div>
              
              {/* Instruments Preview - Now Clickable */}
              <div>
                <PageEditable path="instrumentsHeading" as="h2" className="text-2xl font-bold mb-6" fallback="Instruments" />
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                  {instruments.map((inst) => (
                    <InstrumentCard
                      key={inst.id}
                      instrument={inst}
                      selected={false}
                      onClick={() => {
                        setSelectedInstrument(inst.id);
                        setView('schedule');
                      }}
                      adminEditing={adminEditing}
                      onEditName={saveInstrumentName}
                      onEditDescription={saveInstrumentDescription}
                      onEditImage={openInstrumentImageModal}
                    />
                  ))}
                </div>
                {adminEditing && (
                  <div className="mt-6 flex justify-center">
                    <button
                      onClick={async () => {
                        const name = window.prompt('New instrument name?');
                        if (!name) return;
                        try {
                          const token = localStorage.getItem('auth_token');
                          const id = name.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '-' + Date.now().toString().slice(-4);
                          await axios.post(`${API}/api/live-lessons/instruments`, {
                            id, name, image: '', description: '',
                          }, { headers: { Authorization: `Bearer ${token}` } });
                          const r = await axios.get(`${API}/api/live-lessons/instruments`);
                          setInstruments(r.data || []);
                          toast.success('Instrument added');
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'Failed to add');
                        }
                      }}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-yellow-400/20 hover:bg-yellow-400/30 border-2 border-dashed border-yellow-400/70 rounded-2xl text-yellow-100 font-semibold"
                      data-testid="add-instrument-btn"
                    >
                      <Plus className="w-4 h-4" /> Add instrument
                    </button>
                  </div>
                )}
              </div>
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* Booking Modal */}
      <AnimatePresence>
        {bookingSlot && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
            onClick={() => setBookingSlot(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-stone-800 rounded-2xl p-6 max-w-md w-full border border-stone-700"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold mb-4">Book Lesson</h3>
              
              <div className="bg-stone-700/50 rounded-lg p-4 mb-4">
                <p className="text-sm text-muted-foreground">
                  {new Date(bookingSlot.date).toLocaleDateString('en-US', { 
                    weekday: 'long', month: 'long', day: 'numeric' 
                  })}
                </p>
                <p className="text-2xl font-bold text-primary">{bookingSlot.time}</p>
                <p className="text-sm text-muted-foreground">
                  {bookingSlot.duration} minutes • ${bookingSlot.price}
                </p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Your Name *</label>
                  <input
                    type="text"
                    value={bookingForm.name}
                    onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <input
                    type="email"
                    value={bookingForm.email}
                    onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Notes (Optional)</label>
                  <textarea
                    value={bookingForm.notes}
                    onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })}
                    rows={2}
                    placeholder="Any specific topics you'd like to focus on?"
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none resize-none"
                  />
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setBookingSlot(null)}
                    className="flex-1 py-2 bg-stone-700 rounded-lg hover:bg-stone-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBookSlot}
                    className="flex-1 py-2 bg-primary text-black font-bold rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    Confirm Booking
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Session Modal (for teachers/admin) */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-stone-800 rounded-2xl p-6 max-w-md w-full border border-stone-700"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold mb-4">Start Live Session</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Session Title</label>
                  <input
                    type="text"
                    value={createForm.title}
                    onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Instrument Focus</label>
                  <select
                    value={createForm.instrument}
                    onChange={(e) => setCreateForm({ ...createForm, instrument: e.target.value })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                  >
                    <option value="">All Instruments</option>
                    {instruments.map((inst) => (
                      <option key={inst.id} value={inst.name}>{inst.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Max Students</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={createForm.max_students}
                    onChange={(e) => setCreateForm({ ...createForm, max_students: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                  />
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 py-2 bg-stone-700 rounded-lg hover:bg-stone-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateSession}
                    className="flex-1 py-2 bg-primary text-black font-bold rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
                  >
                    <Radio className="w-4 h-4" />
                    Go Live
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Admin: Change instrument image */}
      {instrumentImageModal && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80"
          onClick={() => setInstrumentImageModal(null)}
        >
          <div
            className="w-full max-w-lg bg-stone-900 border border-yellow-400/50 rounded-2xl p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-white mb-1">Change instrument image</h3>
            <p className="text-xs text-muted-foreground mb-4">{instrumentImageModal.name}</p>
            {instrumentImageDraft && (
              <img loading="lazy" decoding="async" src={instrumentImageDraft} alt="preview" className="w-full h-32 object-cover rounded-lg mb-3 bg-stone-800" />
            )}
            <div className="border border-dashed border-stone-700 rounded-lg p-3 mb-3 text-center">
              <input
                id="instrument-image-file"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setUploadingInstImg(true);
                  try {
                    const token = localStorage.getItem('auth_token');
                    const fd = new FormData();
                    fd.append('file', file);
                    const res = await axios.post(`${API}/api/admin/hero-slides/upload`, fd, { headers: { Authorization: `Bearer ${token}` } });
                    setInstrumentImageDraft(res.data?.url || '');
                    toast.success('Uploaded — click Save');
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Upload failed');
                  } finally {
                    setUploadingInstImg(false);
                  }
                }}
              />
              <label
                htmlFor="instrument-image-file"
                className={`inline-flex items-center gap-2 px-4 py-2 bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-lg text-sm cursor-pointer ${uploadingInstImg ? 'opacity-50 pointer-events-none' : ''}`}
              >
                {uploadingInstImg ? 'Uploading…' : 'Upload from device'}
              </label>
            </div>
            <label className="text-xs text-muted-foreground mb-1 block">Or paste image URL</label>
            <input
              value={instrumentImageDraft}
              onChange={(e) => setInstrumentImageDraft(e.target.value)}
              placeholder="https://…"
              className="w-full px-3 py-2 bg-stone-800 border border-stone-700 rounded-lg text-white mb-4"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setInstrumentImageModal(null)} className="px-3 py-2 text-sm text-gray-300 hover:text-white">Cancel</button>
              <button
                onClick={async () => {
                  await saveInstrumentGeneric(instrumentImageModal, { image: instrumentImageDraft.trim() });
                  setInstrumentImageModal(null);
                }}
                className="px-4 py-2 text-sm bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-lg"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
